import * as React from 'react';

import classnames from 'classnames';

import styles from './image-gallery.module.css';

type Props = {
    images: string[] | null | undefined;
    isOutbound: boolean;
    isModalOpen: boolean;
    setIsModalOpen: (arg1: boolean) => void;
};

type ImageState = {
    loaded: boolean;
    error: boolean;
};

export function ImageGallery(props: Props) {
    const {images, isOutbound, isModalOpen, setIsModalOpen} = props;
    const [imageStates, setImageStates] = React.useState<{
        [key: string]: ImageState;
    }>({});
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

    const handleKeyDown = (e: KeyboardEvent) => {
        if (!isModalOpen) return;

        if (e.key === 'Escape') {
            handleCloseModal();
        } else if (e.key === 'ArrowLeft') {
            handlePrevImage();
        } else if (e.key === 'ArrowRight') {
            handleNextImage();
        }
    };

    React.useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    if (!images || images.length === 0) {
        return null;
    }

    const handleImageLoad = (imageUrl: string) => {
        setImageStates((prev) => ({
            ...prev,
            [imageUrl]: {loaded: true, error: false},
        }));
    };

    const handleImageError = (imageUrl: string) => {
        setImageStates((prev) => ({
            ...prev,
            [imageUrl]: {loaded: true, error: true},
        }));
    };

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    };

    const getImageState = (imageUrl: string): ImageState => {
        return imageStates[imageUrl] || {loaded: false, error: false};
    };

    const getVisibleImages = () => {
        return images.slice(0, 4);
    };

    const renderImage = (imageUrl: string, index: number, isLast: boolean) => {
        const state = getImageState(imageUrl);
        const imageKey = `${imageUrl}-${index}`;
        const hasMoreImages = images.length > 4 && isLast;

        const imageStylenames = classnames({
            [styles['gallery-image']]: true,
            [styles['gallery-image--loading']]: !state.loaded,
            [styles['gallery-image--error']]: state.error,
            [styles['gallery-image--clickable']]: !state.error,
        });

        return (
            <div
                key={imageKey}
                onClick={() => handleImageClick(index)}
                className={styles['image-container']}
            >
                {!state.loaded && !state.error && (
                    <div className={styles['image-placeholder']}>
                        <div className={styles['loading-spinner']} />
                    </div>
                )}
                {state.error ? (
                    <div className={styles['image-error']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <div className={styles['error-text']}>Failed to load</div>
                    </div>
                ) : (
                    <>
                        <img
                            src={imageUrl}
                            alt={`Image ${index + 1}`}
                            onLoad={() => handleImageLoad(imageUrl)}
                            onError={() => handleImageError(imageUrl)}
                            style={{display: state.loaded ? 'block' : 'none'}}
                            className={imageStylenames}
                        />
                        {hasMoreImages && state.loaded && (
                            <div className={styles['more-images-overlay']}>
                                +{images.length - 4}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const getGridStyleName = () => {
        const count = Math.min(images.length, 4); // Cap at 4 for grid layout
        if (count === 1) return 'gallery-grid--single';
        if (count === 2) return 'gallery-grid--double';
        if (count === 3) return 'gallery-grid--triple';

        return 'gallery-grid--multiple';
    };

    const galleryStylenames = classnames({
        [styles.gallery]: true,
        [styles['gallery--outbound']]: isOutbound,
    });

    const gridStylenames = classnames({
        [styles['gallery-grid']]: true,
        [styles[getGridStyleName()]]: true,
    });

    const visibleImages = getVisibleImages();

    return (
        <>
            <div className={galleryStylenames}>
                <div className={gridStylenames}>
                    {visibleImages.map((imageUrl, index) =>
                        renderImage(imageUrl, index, index === visibleImages.length - 1)
                    )}
                </div>
                {images.length > 1 && (
                    <div className={styles['image-count']}>
                        {images.length} image{images.length > 1 ? 's' : ''}
                    </div>
                )}
            </div>
            {isModalOpen && (
                <div onClick={handleCloseModal} className={styles['modal-overlay']}>
                    <div onClick={(e) => e.stopPropagation()} className={styles['modal-content']}>
                        <button onClick={handleCloseModal} className={styles['modal-close']}>
                            ×
                        </button>
                        <div className={styles['modal-image-container']}>
                            <img
                                src={images[currentImageIndex]}
                                alt={`Image ${currentImageIndex + 1}`}
                                className={styles['modal-image']}
                            />
                        </div>
                        {images.length > 1 && (
                            <>
                                <button
                                    onClick={handlePrevImage}
                                    className={classnames(
                                        styles['modal-nav'],
                                        styles['modal-nav--prev']
                                    )}
                                >
                                    ‹
                                </button>
                                <button
                                    onClick={handleNextImage}
                                    className={classnames(
                                        styles['modal-nav'],
                                        styles['modal-nav--next']
                                    )}
                                >
                                    ›
                                </button>

                                <div className={styles['modal-counter']}>
                                    {currentImageIndex + 1} / {images.length}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
